import React, { useState, useEffect, useContext } from "react";
import {
    Dialog,
    Form,
    Divider,
    RadioGroup,
    FormButton,
    FormCheckbox,
    CheckboxProps,
    Provider,
    Flex,
    FlexItem,
    Button,
    EditIcon,
    Text,
    Pill,
    RadioGroupItemProps,
    AddIcon,
    Input,
    TrashCanIcon,
    Tooltip,
    CloseIcon,
    ThemePrepared,
    TeamsThemeStylesProps,
    PillProps,
} from "@fluentui/react-northstar";
import { DatePicker, createTheme, ThemeProvider } from "@fluentui/react";
import { initializeIcons } from "office-ui-fabric-react/lib/Icons";
import { IProject, IUser } from "../types/types";
import { ApiContext } from "../providers/ApiProvider";
initializeIcons(/* optional base url */);

interface IConfigureProjectDialogTypes {
    user: IUser;
    projectName: string | undefined | null;
    projectId: string | undefined;
    jwtoken: string;
    selected: boolean;
    clearStates: () => void;
    setMessage: (msg: string) => void;
    setSuccess: (success: boolean) => void;
}

interface ExtendedPillProps extends PillProps {
    children: React.ReactNode;
}

const myTheme = createTheme({
    palette: {
        themePrimary: "#6264a7",
        themeLighterAlt: "#040407",
        themeLighter: "#10101b",
        themeLight: "#1d1e32",
        themeTertiary: "#3b3c63",
        themeSecondary: "#565892",
        themeDarkAlt: "#6e70af",
        themeDark: "#8183bb",
        themeDarker: "#9ea0cd",
        neutralLighterAlt: "#323130",
        neutralLighter: "#3a3938",
        neutralLight: "#484645",
        neutralQuaternaryAlt: "#504f4d",
        neutralQuaternary: "#575554",
        neutralTertiaryAlt: "#747270",
        neutralTertiary: "#f3f1f0",
        neutralSecondary: "#f5f4f2",
        neutralPrimaryAlt: "#f7f6f5",
        neutralPrimary: "#edebe9",
        neutralDark: "#fbfafa",
        black: "#979593",
        white: "#292827",
    },
});

export default function ConfigureProjectDialog(props: IConfigureProjectDialogTypes) {
    const { getProjectById, updateProjectSettings, deleteProject } = useContext(ApiContext);
    const [disabled, setDisabled] = useState<boolean>(true);
    const [projectName, setProjectName] = useState<string | undefined>();
    const [popup, setPopup] = useState<boolean>(false);
    const [projectSettings, setProjectSettings] = useState<IProject | null>(null);
    const [isPrivate, setIsPrivate] = useState<boolean>(true);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [locked, setLocked] = useState<boolean | undefined | "mixed">(false);
    const [tags, setTags] = useState<string[]>(["Add"]);
    const [memberList, setMemberList] = useState<string[]>([]);
    const [input, setInput] = useState("");
    const [alwaysOpen, setAlwaysOpen] = useState<boolean>(true);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [budgetTotal, setBudgetTotal] = useState<number | null>(null);
    const [budgetUsed, setBudgetUsed] = useState<number | null>(null);
    const [projectId, setProjectId] = useState<string | undefined>(undefined);

    useEffect(() => {
        if (projectSettings !== null) {
            console.info("projectSettings", projectSettings);
            const _p = projectSettings;
            const activeFrom = _p.active.from!;
            const activeTo = _p.active.to!;
            if (activeTo === null && activeFrom === null) setAlwaysOpen(true);
            else {
                setAlwaysOpen(false);
                setStartDate(new Date(activeFrom));
                setEndDate(new Date(activeTo));
            }

            setLocked(_p.entry_locked);
            setIsPrivate(_p.private);
            setTags(_p.tags);
            setMemberList(_p.members);
            if (_p.budget) {
                setBudgetUsed(_p.budget.used);
                setBudgetTotal(_p.budget.total);
            }
        }
    }, [projectSettings]);

    useEffect(() => {
        if (props.projectName) setProjectName(props.projectName);
    }, [props.projectName]);

    useEffect(() => {
        if (props.projectId) setProjectId(props.projectId);
    }, [props.projectId]);

    useEffect(() => {
        setDisabled(!props.selected);
    }, [props.selected]);

    const handleProjectEdit = async () => {
        try {
            const settings = await getProjectById(props.jwtoken, props.projectId);
            setProjectSettings(settings);
            setPopup(true);
        } catch (error) {
            console.error(error);
            setPopup(false);
        }
    };

    const handleSubmit = async () => {
        try {
            if (projectSettings !== null) {
                console.info(`Project: ${projectName}`);
                console.info(`  Start date: ${startDate}`);
                console.info(`  End date: ${endDate}`);
                console.info(`  Locked: ${locked}`);
                console.info(`  Tags: ${tags}`);
                console.info(`  Private: ${isPrivate}`);
                console.info(`  Owner OID: ${projectSettings.owner_oid}`);
                console.info(`  Members: ${memberList}`);
                console.info(`  AlwaysOpen: ${alwaysOpen}`);
                console.info(`  Budget total: ${budgetTotal}`);
                console.info(`  Budget used: ${budgetUsed}`);

                const body: IProject = projectSettings; //Init with prev values
                body.project_name = projectName ? projectName : body.project_name;
                body.entry_locked = locked ? true : false;
                body.tags = tags;
                body.private = isPrivate;
                body.members = memberList;
                body.active.from = startDate ? startDate.valueOf() : null;
                body.active.to = endDate ? endDate.valueOf() : null;

                // If budget is missing add it manually
                if (!body.budget || !body.budget.total || !body.budget.used) {
                    const budget = { total: null, used: null };
                    body.budget = budget;
                }
                body.budget.total = budgetTotal;
                body.budget.used = budgetUsed;

                // If closet or set to always open set active hours to null
                if (alwaysOpen && !locked) {
                    body.active.from = null;
                    body.active.to = null;
                }

                await updateProjectSettings(props.jwtoken, body, projectId)
                    .then(() => {
                        props.setSuccess(true);
                        // if (typeof ret.message === "string") props.setMessage(ret.message);
                        props.setMessage("Project settings updated successfully");
                    })
                    .catch((err) => {
                        props.setSuccess(false);
                        props.setMessage(err.message);
                    });
            } else {
                throw new Error("Project settings is null");
            }
        } catch (error) {
            console.error(error);
        }
        setPopup(false);
    };

    const handleAddTag = (e: { preventDefault: () => void }) => {
        e.preventDefault();
        const trimmedInput = input.trim();
        setTags((prevState) => [...prevState, trimmedInput]);
        setInput("");
    };
    const handleDeleteProject = () => {
        setShowAlert(true);
    };

    const handleAlertCancel = () => {
        setShowAlert(false);
    };

    const handleAlertConfirm = () => {
        setShowAlert(false);
        // Delete project
        deleteProject(props.jwtoken, projectId)
            .then(() => {
                props.setSuccess(true);
                // if (typeof ret.message === "string") props.setMessage(ret.message);
                // else
                props.setMessage("Project deleted successfully");
                setPopup(false);
                setProjectName(undefined);
                props.clearStates();
            })
            .catch((err) => {
                props.setSuccess(false);
                props.setMessage(err.message);
                setPopup(false);
            });
    };

    return (
        <Provider.Consumer
            render={(theme: ThemePrepared<TeamsThemeStylesProps>) => (
                <Dialog
                    key="dialog"
                    backdrop
                    open={popup}
                    trapFocus
                    closeOnOutsideClick={false}
                    styles={{
                        color: theme.siteVariables.colorScheme.brand.foreground,
                        overflow: "auto",
                    }}
                    content={
                        <Form
                            onSubmit={async (e) => {
                                e.preventDefault();
                                handleSubmit();
                            }}
                            onReset={() => {
                                setPopup(false);
                            }}
                        >
                            <Flex gap="gap.large" column={false}>
                                <FlexItem>
                                    {/* <Text color={"red"} weight="bold" size="largest" content={`${projectName}`} /> */}
                                    <Input
                                        variables={{
                                            inputPadding: "0.5rem",
                                        }}
                                        input={{
                                            style: {
                                                textAlign: "left",
                                                fontSize: "30px",
                                                fontWeight: "bold",
                                                minHeight: "50px",
                                                maxWidth: "80%",
                                                color: !isPrivate ? "grey" : theme.siteVariables.colors.red["400"],
                                            },
                                        }}
                                        autoComplete="off"
                                        type="text"
                                        placeholder={`${projectName}`}
                                        id={`${projectName}-id`}
                                        value={projectName}
                                        onChange={(e) => {
                                            const target = e.target as HTMLTextAreaElement;
                                            setProjectName(target.value);
                                        }}
                                        disabled={!isPrivate}
                                        inline
                                    />
                                </FlexItem>
                                <Dialog
                                    trapFocus
                                    backdrop
                                    closeOnOutsideClick={false}
                                    open={showAlert}
                                    onCancel={handleAlertCancel}
                                    onConfirm={handleAlertConfirm}
                                    confirmButton="Confirm"
                                    content="Are you sure you want to delete this project? If you are not 100% sure, don't do this!"
                                    header="Action confirmation"
                                    headerAction={{
                                        icon: <CloseIcon />,
                                        title: "Close",
                                        onClick: handleAlertCancel,
                                    }}
                                    trigger={
                                        <FlexItem>
                                            <Tooltip
                                                trigger={
                                                    <FormButton
                                                        disabledFocusable={!isPrivate || !projectSettings?.private}
                                                        primary
                                                        id="button_delete"
                                                        type="button"
                                                        iconOnly
                                                        size="medium"
                                                        key="deleteProjectIconButton"
                                                        icon={<TrashCanIcon />}
                                                        onClick={handleDeleteProject}
                                                    />
                                                }
                                                content={"Delete project. (Only available for private projects"}
                                            />
                                        </FlexItem>
                                    }
                                />
                            </Flex>
                            <Divider />
                            {props.user.is_admin || props.user.is_project_manager ? (
                                <>
                                    <Flex gap="gap.medium" column>
                                        <Text weight="bold" size="large" content="Set active hours / locked state" />
                                        <FormCheckbox
                                            label="Entry locked"
                                            toggle
                                            onChange={(e, data: CheckboxProps = {}) => {
                                                setLocked(data.checked);
                                                setAlwaysOpen(false);
                                            }}
                                            disabled={disabled || !props.user.is_admin}
                                            checked={locked ? true : false}
                                        />
                                        {locked ? (
                                            <></>
                                        ) : (
                                            <FlexItem>
                                                <FormCheckbox
                                                    label="Always open"
                                                    toggle
                                                    onChange={(e, data: CheckboxProps = {}) => {
                                                        setAlwaysOpen(data.checked ? true : false);
                                                    }}
                                                    disabled={disabled || !props.user.is_admin}
                                                    checked={alwaysOpen ? true : false}
                                                />
                                            </FlexItem>
                                        )}
                                        {locked || alwaysOpen ? (
                                            <></>
                                        ) : (
                                            <FlexItem>
                                                <ThemeProvider
                                                    style={{
                                                        maxWidth: "200px",
                                                    }}
                                                    applyTo="body"
                                                    theme={myTheme}
                                                >
                                                    <DatePicker
                                                        label="Select active start date"
                                                        onSelectDate={(date) => {
                                                            if (date) setStartDate(date);
                                                        }}
                                                        disabled={
                                                            locked ||
                                                            (alwaysOpen ? true : false) ||
                                                            !props.user.is_admin
                                                        }
                                                        initialPickerDate={startDate === null ? undefined : startDate}
                                                        showGoToToday
                                                        allowTextInput
                                                        value={startDate === null ? undefined : startDate}
                                                        ariaLabel="Select startdate"
                                                        isRequired
                                                    />
                                                    <DatePicker
                                                        label="Select active end date"
                                                        onSelectDate={(date) => {
                                                            if (date) setEndDate(date);
                                                        }}
                                                        disabled={
                                                            locked ||
                                                            (alwaysOpen ? true : false) ||
                                                            !props.user.is_admin
                                                        }
                                                        initialPickerDate={endDate === null ? undefined : endDate}
                                                        showGoToToday
                                                        allowTextInput
                                                        value={endDate === null ? undefined : endDate}
                                                        ariaLabel="Select enddate"
                                                        isRequired
                                                    />
                                                </ThemeProvider>
                                            </FlexItem>
                                        )}
                                    </Flex>
                                    <RadioGroup
                                        vertical
                                        defaultCheckedValue={isPrivate ? "private" : "public"}
                                        items={
                                            props.user.is_admin
                                                ? ["private", "public"].map((group) => ({
                                                      key: group,
                                                      value: group,
                                                      name: "group",
                                                      label: group,
                                                      "aria-label": "orange",
                                                  }))
                                                : []
                                        }
                                        onCheckedValueChange={(e, p: RadioGroupItemProps | undefined) => {
                                            const members: string[] = [];
                                            if (p!.value == "public") {
                                                members.push("offcodeGroup");
                                                setIsPrivate(false);
                                            } else {
                                                members.push(props.user.oid);
                                                setIsPrivate(true);
                                            }
                                            setMemberList(members);
                                        }}
                                    />
                                    <Divider />
                                    <Text weight="bold" size="large" content="Tags" />
                                    <Flex gap="gap.small">
                                        <Input
                                            value={input}
                                            placeholder="Enter new tag"
                                            onChange={(e, data) => {
                                                if (data) setInput(data.value);
                                            }}
                                            disabled={!props.user.is_admin}
                                        />
                                        <Button
                                            disabledFocusable={disabled || !props.user.is_admin}
                                            iconOnly
                                            size="medium"
                                            key="editCompanyNameButton"
                                            icon={<AddIcon />}
                                            id="1"
                                            onClick={handleAddTag}
                                        />
                                    </Flex>
                                    <Flex gap="gap.medium">
                                        {tags.map((e, i) => {
                                            return (
                                                <Pill
                                                    disabled={!props.user.is_admin}
                                                    key={`${e}_${i}`}
                                                    actionable
                                                    size={"small"}
                                                    onDismiss={(e, data) => {
                                                        if (data && (data as ExtendedPillProps).children) {
                                                            console.log(e);
                                                            console.log(data);
                                                            console.log(tags);
                                                            setTags(
                                                                tags.filter(
                                                                    (tag) =>
                                                                        tag !== (data as ExtendedPillProps).children
                                                                )
                                                            ); //remove tag from tags
                                                        }
                                                    }}
                                                >
                                                    {e}
                                                </Pill>
                                            );
                                        })}
                                    </Flex>
                                    <Divider />
                                </>
                            ) : null}
                            <Text
                                // color={"red"}
                                weight="bold"
                                size="large"
                                content={"Budget"}
                            />
                            <Input
                                label="Total"
                                value={budgetTotal?.toString()}
                                disabled={disabled || !props.user.is_admin}
                                type={"number"}
                                onChange={(e: any) => setBudgetTotal(parseFloat(e.target.value))}
                                placeholder="Enter budget value.."
                            />
                            <Input label="Used" value={budgetUsed?.toFixed(2)} disabled placeholder="0" />
                            <Divider />

                            <Flex>
                                <FormButton
                                    primary
                                    content="Submit"
                                    disabled={disabled || !props.user.is_admin}
                                    id="button_submit"
                                    type="submit"
                                />
                                <FlexItem push>
                                    <FormButton secondary content="Cancel" id="button_cancel" type="reset" />
                                </FlexItem>
                            </Flex>
                        </Form>
                    }
                    header={<div>Configure project settings</div>}
                    trigger={
                        <Button
                            disabledFocusable={disabled}
                            key="editCompanyNameButton"
                            icon={<EditIcon />}
                            id="1"
                            onClick={handleProjectEdit}
                            disabled={disabled}
                            content="Edit"
                        />
                    }
                />
            )}
        />
    );
}
